/**
 * 「新建项目」那条路的驱动逻辑（验收 #49 #50 #51 #53）。
 *
 * # 为什么它是一个独立的纯函数模块
 *
 * 这条路上一版**整个不存在**：`createNamed` 建完项目立刻
 * `startConversation('solo', created.id)` 就跳走了，三阶段那一屏在真实流程里
 * 没有出现的时机。台账把 #49/#50 标成 `todo` 时写的正是这句话。
 *
 * 而它不能靠「在 index.tsx 里多写三行」补上：这条路要回答的问题有四个，
 * 每一个都有一个错法会静默地把事情做坏：
 *
 *   1. **什么时候才该建会话** —— 早了会得到一个不能派活的会话（设计稿 Z11
 *      原话：「仓库没好就创建会话，会得到一个不能派活的会话」）
 *   2. **关页之后再进来看到什么** —— 从头播一遍是撒谎，服务端知道真实阶段
 *   3. **重试补哪一步** —— 从头来会用掉一把新钥匙，那正是「又建了一个项目」的来路
 *   4. **建好了往哪儿跳** —— 会话 id 可能不在这台机器上（在另一个标签页里建的）
 *
 * 把它们放在纯函数里，四个问题各自有样本；放在 effect 里，它们只有
 * 「跑起来看看」这一种验法。
 */

import {
  isReadableInitStatus,
  stageIdempotencyKey,
  type WireInitStatus,
} from './provisioning'

/**
 * 正在建的那个项目。**存在 localStorage 里，不是 React state。**
 *
 * 设计稿 Z11 给了一条硬承诺：「关掉这页 · 准备继续在后台跑」「再回来 ·
 * 回到这一页看到的是当时的真实阶段，不从头播一遍」。React state 关页就没了，
 * 而没了之后用户手上会多一个建了一半、他不知道存在的项目。
 *
 * `rootKey` 是三步共用的那把钥匙。它必须**存住**——每次点重试现生成一把，
 * 就等于每次都建一个新项目，而那正是这把钥匙要挡的事。
 */
export interface ProvisioningRecord {
  readonly projectId: string
  readonly name: string
  readonly rootKey: string
  /** 会话建出来之后记住它，跳转要用。还没建就是 null。 */
  readonly conversationId: string | null
}

export const PROVISIONING_STORAGE_KEY = 'npc.lulu.provisioning'

/**
 * 最小存储面。传接口而不是直接摸 `window.localStorage`，
 * 是因为无痕模式下读写都会抛，而这条路在那时候必须还能走
 * （降级成「这一次不记住」，不是整页崩掉）。
 */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export function readProvisioning(store: KeyValueStore | null): ProvisioningRecord | null {
  if (!store) return null
  let raw: string | null
  try {
    raw = store.getItem(PROVISIONING_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<ProvisioningRecord>
    // 逐字段校验，不信任 localStorage 里躺着的东西：它可能是上一个版本
    // 写的、可能是别人手改的。缺字段就当没有——一条半截的记录会让这条路
    // 卡在一个说不清的状态里。
    if (typeof v.projectId !== 'string' || v.projectId === '') return null
    if (typeof v.name !== 'string') return null
    if (typeof v.rootKey !== 'string' || v.rootKey === '') return null
    const conversationId = typeof v.conversationId === 'string' && v.conversationId !== ''
      ? v.conversationId
      : null
    return { projectId: v.projectId, name: v.name, rootKey: v.rootKey, conversationId }
  } catch {
    return null
  }
}

export function writeProvisioning(store: KeyValueStore | null, rec: ProvisioningRecord): void {
  if (!store) return
  try {
    store.setItem(PROVISIONING_STORAGE_KEY, JSON.stringify(rec))
  } catch {
    // 无痕模式：记不住就记不住，这条路本轮照样走得完。
  }
}

export function clearProvisioning(store: KeyValueStore | null): void {
  if (!store) return
  try {
    store.removeItem(PROVISIONING_STORAGE_KEY)
  } catch {
    // 同上。
  }
}

/**
 * 下一步该干什么。
 *
 * **判据全部来自服务端那份 wire，一条都不来自本地推断。** 本地一旦开始推断，
 * 就会在服务端说 pending 的时候说「已准备好」——而那正是这一整套要挡的事。
 */
export type ProvisioningNext =
  /** 还没到能动的时候。什么都别做，页面上照实显示阶段。 */
  | { readonly kind: 'wait' }
  /** 前置齐了、会话还没有：现在建它（第 3 步）。 */
  | { readonly kind: 'create_conversation' }
  /** 全好了，进这条会话。 */
  | { readonly kind: 'enter'; readonly conversationId: string }
  /**
   * 全好了，但**这台机器不知道是哪条会话**——在另一个标签页/另一台设备上
   * 建的。调用方去项目的会话列表里找最新那条。
   *
   * 不合并进 `wait`：那会让这条路在「服务端说好了」之后永远停在这一页，
   * 而屏幕上每一格都是绿的。一个说「好了」却不往下走的页面，
   * 比一个说「还在确认」的页面难查得多。
   */
  | { readonly kind: 'enter_unknown' }
  /** 归档了 / 服务端明说这个项目走不下去。停下来说清楚，不要空转。 */
  | { readonly kind: 'blocked' }

export function nextProvisioningStep(
  wire: unknown,
  record: ProvisioningRecord | null,
): ProvisioningNext {
  if (!record) return { kind: 'wait' }
  // 读不到就不动。**不兜底**——一个编出来的阶段推不出一个该做的动作。
  if (!isReadableInitStatus(wire)) return { kind: 'wait' }
  const w = wire as WireInitStatus & { readonly archived?: boolean }
  if (w.archived === true) return { kind: 'blocked' }
  if (w.stages.conversation === 'ready') {
    return record.conversationId
      ? { kind: 'enter', conversationId: record.conversationId }
      : { kind: 'enter_unknown' }
  }
  // 会话还没有。**环境没齐就先别建**：设计稿 Z11 把顺序写成了理由——
  // 「仓库没好就创建会话，会得到一个不能派活的会话」。
  if (w.stages.environments !== 'ready') return { kind: 'wait' }
  return { kind: 'create_conversation' }
}

/**
 * 建项目那一步用的键。
 *
 * 单独给一个函数而不是让调用方拼，是因为**这条派生链中间不许有随机**
 * （CLAUDE.md §8「派生链必须一路算到底」）。它的失效方式很安静：
 * 键不一样了不会报错，只会在某次崩溃后的重试里多建一个项目。
 */
export function projectCreateKey(rootKey: string): string {
  return stageIdempotencyKey(rootKey, 'project')
}

export function conversationCreateKey(rootKey: string): string {
  return stageIdempotencyKey(rootKey, 'conversation')
}
